import {
  Breakpoints,
  Button,
  ButtonColor,
  ButtonSize,
  compact,
  formatName,
  pluralize,
  ProfileImage,
  Rating,
  RatingSize,
  TagColor,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import React, { type Ref, useMemo } from 'react';

import { GoalName } from '../../__generated-gql-types__/globalTypes';
import { getCoachUrl } from '../../utils/coach';
import { generateSearchPath } from '../../utils/search';
import Breadcrumbs, {
  type BreadcrumbsProps,
  goalNameToBreadcrumbsLabel,
} from '../breadcrumbs/Breadcrumbs';
import CompanyTag from '../coach/CompanyTag';
import SchoolTag from '../coach/SchoolTag';

import type { OfferingPackageBannerOfferingPackageFragment } from './__generated-gql-types__/OfferingPackageBanner.generated';

interface OfferingPackageBannerProps {
  offeringPackage: OfferingPackageBannerOfferingPackageFragment;
  viewAllReviews: () => void;
  innerRef?: Ref<HTMLDivElement>;
}

const OfferingPackageBanner: React.FC<OfferingPackageBannerProps> = ({
  offeringPackage,
  viewAllReviews,
  innerRef,
}) => {
  const isSm = useMediaQuery(Breakpoints.SM);

  const { category } = offeringPackage;
  const categorySpecificHeadline = useMemo(
    () =>
      category?.id
        ? offeringPackage.coach.categoryInformationList?.find(
            (c) => c.category.id === category.id,
          )?.headline
        : null,
    [category?.id, offeringPackage.coach.categoryInformationList],
  );

  const breadcrumbs: BreadcrumbsProps['breadcrumbs'] = useMemo(
    () =>
      [
        {
          label: goalNameToBreadcrumbsLabel(
            category?.goal ?? GoalName.SCHOOL,
            isSm,
          ),
          url: generateSearchPath({
            goal: category?.goal ?? GoalName.SCHOOL,
            categorySlug: null,
            subCategorySlug: null,
          }),
        },
        category
          ? {
              label: category.name,
              url: generateSearchPath({
                goal: category.goal,
                categorySlug: category.slug,
                subCategorySlug: null,
              }),
            }
          : null,
      ].filter(compact),
    [category, isSm],
  );

  return (
    <div ref={innerRef} className="pt-4">
      {isSm ? (
        <Breadcrumbs
          className="mx-auto max-w-page px-6"
          breadcrumbs={breadcrumbs}
        />
      ) : null}
      <div className="mx-auto grid max-w-page grid-cols-12 px-4 sm:gap-14 sm:py-5 lg:px-6">
        <div className="col-span-12 sm:col-span-8">
          <h1 className="hidden text-4xl font-medium text-leland-gray-dark sm:block">
            {offeringPackage.name}
          </h1>

          <div
            className={`flex flex-col items-start justify-start ${offeringPackage.isUniversalPackage ? '' : 'sm:mt-6'} sm:flex-row sm:items-center sm:space-x-4`}
          >
            {offeringPackage.isUniversalPackage ? null : (
              <div
                className={`flex w-full items-start justify-between sm:w-fit`}
              >
                <ProfileImage
                  className="size-20 border-2 border-white sm:size-12 sm:border-none"
                  {...offeringPackage.coach.user}
                />
                {category ? (
                  <Link
                    href={getCoachUrl({
                      coachSlug: offeringPackage.coach.slug,
                      categorySlug: category.slug,
                    })}
                    className={`block sm:hidden ${
                      offeringPackage.imageLink ? 'mt-8' : ''
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      label="View profile"
                      buttonColor={ButtonColor.WHITE}
                      size={ButtonSize.SMALL}
                    />
                  </Link>
                ) : null}
              </div>
            )}
            {isSm ? null : (
              <Breadcrumbs className="mt-6" breadcrumbs={breadcrumbs} />
            )}
            <h1 className="mt-4 block text-3xl font-medium text-leland-gray-dark sm:hidden">
              {offeringPackage.name}
            </h1>
            {offeringPackage.isUniversalPackage ? null : (
              <div className="mt-4 flex flex-col items-start justify-center sm:mt-0">
                <div className="flex items-center justify-start space-x-2">
                  <h3 className="text-lg font-medium text-leland-gray-dark">
                    Offered by {formatName(offeringPackage.coach.user)}
                  </h3>
                  {offeringPackage.coach.reviewTotal > 0 ? (
                    <div className="flex items-center justify-start space-x-1">
                      <div className="text-leland-gray-light">
                        <Rating
                          totalStars={1}
                          rate={offeringPackage.coach.averageReviewRate}
                          showRate
                          size={RatingSize.SMALL}
                        />
                      </div>
                      <span className="text-leland-gray-light">
                        (
                        <button
                          type="button"
                          className="link"
                          onClick={viewAllReviews}
                        >
                          {pluralize(
                            offeringPackage.coach.reviewTotal,
                            'review',
                          )}
                        </button>
                        )
                      </span>
                    </div>
                  ) : null}
                  {category ? (
                    <Link
                      href={getCoachUrl({
                        coachSlug: offeringPackage.coach.slug,
                        categorySlug: category.slug,
                      })}
                      className="link link--primary hidden text-lg font-medium lg:block"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View profile
                    </Link>
                  ) : null}
                </div>
                {categorySpecificHeadline ? (
                  <p className="line-clamp-1 text-lg font-normal leading-6 text-leland-gray-light">
                    {categorySpecificHeadline}
                  </p>
                ) : null}
              </div>
            )}
          </div>
          {offeringPackage.isUniversalPackage ? null : (
            <div className="mt-2 flex items-center justify-start space-x-2">
              <SchoolTag
                coach={offeringPackage.coach}
                tagColor={TagColor.TRANSPARENT}
              />
              <CompanyTag
                coach={offeringPackage.coach}
                tagColor={TagColor.TRANSPARENT}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfferingPackageBanner;
